import { Button, Form, Stack } from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Children, FC, Fragment, isValidElement, cloneElement, ReactNode } from 'react';
import { usePortalMode } from '../../../hooks/use-portal-mode';
import { PortalModal } from '../../modals/modals';
import * as Styles from './filter.module.less';
import { portalFilterHasAny } from './filter.utils';

export interface ExtendedFilterFieldProps {
    mobile?: boolean;
    className?: string;
}

interface ExtendedFilterFormProps {
    mobile: boolean;
    children?: ReactNode;
}

const ExtendedFilterForm: FC<ExtendedFilterFormProps> = observer(({ children, mobile }) => {
    const childrenWithProps = Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a typescript error too.
        if (isValidElement(child)) {
            return cloneElement(child, {
                ...child.props,
                mobile,
                className: classNames(
                    Styles.formItem,
                    mobile ? Styles.formTakeoverItem : Styles.formModalItem,
                    child.props.className
                ),
            } as any);
        }
        return child;
    });

    return (
        <Form className={mobile ? Styles.formTakeover : Styles.formModal}>{childrenWithProps}</Form>
    );
});

export interface PortalFilterProps {
    mobile: boolean;
    open?: boolean;
    hasAny: boolean;
    children?: ReactNode;
    apply(): void;
    cancel(): void;
    clear(): void;
}

const PortalFilterComponent: FC<PortalFilterProps> = ({
    apply,
    cancel,
    children,
    clear,
    hasAny,
    mobile,
    open,
}) => {
    return (
        <PortalModal
            mobile={mobile}
            title={mobile ? 'Filters' : 'Edit Filters'}
            open={open ?? false}
            onClose={cancel}
            footerAlign="space-between"
            takeover={{
                backLabel: '',
                theme: 'grey',
                actions: (
                    <Button size="small" fill="subtle" onClick={clear} primary>
                        Reset All
                    </Button>
                ),
            }}
            footer={
                <Fragment>
                    {!mobile && (
                        <Stack.Item fill>
                            <Button fill="subtle" onClick={clear} disabled={!hasAny} primary>
                                Clear All
                            </Button>
                        </Stack.Item>
                    )}
                    <Button
                        className={classNames('m-r-1', { 'flex-grow-1 flex-basis-0': mobile })}
                        onClick={cancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={classNames('m-l-1', { 'flex-grow-1 flex-basis-0': mobile })}
                        onClick={apply}
                        primary
                    >
                        Apply
                    </Button>
                </Fragment>
            }
        >
            <ExtendedFilterForm mobile={mobile}>{children}</ExtendedFilterForm>
        </PortalModal>
    );
};

export const PortalFilter = Object.assign(PortalFilterComponent, {
    hasAny: portalFilterHasAny,
});

const PortalFilterObserverComponent: FC<Omit<PortalFilterProps, 'mobile'>> = observer(props => {
    const mode = usePortalMode();

    return <PortalFilter mobile={mode.mobile} {...props} />;
});

export const PortalFilterObserver = Object.assign(PortalFilterObserverComponent, {
    hasAny: portalFilterHasAny,
});
